import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { get, isNil, isEmpty } from 'lodash';
import { withRouter } from 'react-router-dom';

// local dependencies
import { callControlWidget as CallControlWidgetView } from 'ui/views/widgets/callControlWidget';
import { CallControlWidgetService } from 'services/callControlWidget';
import { updateCallState, initCallState } from 'store/actions/callState';
import {
	SERVICE_RESPONSE_STATUS,
	GLOBAL_LABELS,
	SIZES,
} from 'constants/common';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import { REDUCER } from 'constants/entity-names';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import * as successOverlay from 'utils/successOverlay';
import { getLabel } from 'utils/getLabel';
import * as confirmOverlay from 'utils/confirmOverlay';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { CallControlWidget } from 'ui/containers/CallControlWidget';
 *
 * // render
 * <CallControlWidget propName='propValue'/>
 */
class _CallControlWidget extends React.Component {

	/**
	 * @param {object} props - props
	 */
	constructor( props ) {
		super( props );

		// bind events and methods to instance
		this.handleCallControlActionClick = this.handleCallControlActionClick.bind(
			this
		);
		this.handleCallCategoryAndReasonsChange = this.handleCallCategoryAndReasonsChange.bind(
			this
		);

		// component state
		this.state = {
			isFetching: true,
			networkStatus: undefined,
			componentData: undefined,
			isCallDispositionVisible: false,
			callReasonValidationState: false,
			callNotesValidationState: false,
			autoTaggingCallResolution: null,
			patientId: (this.props.callState && this.props.callState.callerInfo && this.props.callState.callerInfo.patientId ) ? this.props.callState.callerInfo.patientId : null
		};

		// create service instance
		this.callControlWidgetService = new CallControlWidgetService();

		// start component data fetch
		this.fetchComponentData();
	}

	/*********************************************/

	/**
	 * @desc Fetch component response.
	 */
	async fetchComponentData() {
		log.debug( 'CallControlWidget.fetchComponentData()' );

		// cancel ongoing request
		this.callControlWidgetService.cancel();

		// fetch component data
		const {
			status,
			data,
		} = await this.callControlWidgetService.fetchComponentData( {
			url: this.props.widget.url,
			data: {patientId: this.state.patientId}
		} );

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
			} );
		} else {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				componentData: data,
			} );
		}
	}

	/**
	 * @desc Handle the status of the reason and category widget.
	 */
	handleCallCategoryAndReasonsChange( { isValid, callResolution } = {} ) {
		this.setState( {
			callReasonValidationState: isValid,
			autoTaggingCallResolution: callResolution,
		} );
	}

	/**
	 * @desc Handle the status of notes widget
	 */
	handleCallNotesChange = ( { isValid } = {} ) => {
		this.setState( { callNotesValidationState: isValid } );
	}

	/**
	 * @desc Handle call control action click.
	 */
	handleCallControlActionClick( action = {} ) {
		log.debug( 'CallControlWidget.handleCallControlActionClick()' );

		switch ( action.name ) {

			// make inbound call
			case 'initiateInboundCall': {
				return this.handleInitiateInboundCallAction( action );
			}

			// make an outbound call
			case 'initiateOutboundCall': {
				return this.handleInitiateOutboundCallAction( action );
			}

			case 'transferCall': {
				return this.initiateTransferCall( action );
			}

			case 'retrieveCall': {
				return this.initiateRetrieveCall( action );
			}

			// end call
			case 'endCall': {
				return this.handleCallEndAction( action );
			}
		}
	}

	/**
	 * @desc Handle `initiate inbound call` action button click.
	 */
	async handleInitiateInboundCallAction( action = {} ) {
		log.debug( 'CallControlWidget.handleInitiateInboundCallAction()' );

		// cancel ongoing request
		this.callControlWidgetService.cancel();

		// show page level preloader
		pageLevelPreloader.show();

		// get status and service data
		const {
			status,
			data,
		} = await this.callControlWidgetService.initiateInboundCall( {
			url: action.url,
			data: {patientId: this.state.patientId}
		} );

		// hide page level preloader
		pageLevelPreloader.hide();

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
			this.setState(
				{
					isFetching: false,
					networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				},
				() => {
					this.props.initCallState( data ); // initiate call state

					// change to authorization flow route
					this.props.history.push( DASHBOARD_SUB_ROUTES.CALL_AUTHORIZATION_FLOW );
				}
			);
		} else {
			errorOverlay.open(); // show error
		}
	}

	/**
	 * @desc handle `initiate outbound call` action button click.
	 */
	handleInitiateOutboundCallAction() {
		log.debug( 'CallControlWidget.handleInitiateOutboundCallAction()' );

		// change to authorization flow route
		this.props.history.push( DASHBOARD_SUB_ROUTES.CALL_AUTHORIZATION_FLOW );
	}

	/**
	 * @desc: action is executed when MSR clicks on transfer call button
	 */
	async initiateTransferCall( action = {} ) {
		log.debug( 'CallControlWidget.initiateTransferCall()' );

		// cancel ongoing request
		this.callControlWidgetService.cancel();

		// show page level preloader
		pageLevelPreloader.show();

		// get status and service data
		const { status, data } = await this.callControlWidgetService.transferCall( {
			url: action.url,
			data: {patientId: this.state.patientId}
		} );

		// hide page level preloader
		pageLevelPreloader.hide();

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
			this.setState(
				{
					isFetching: false,
					networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				},
				() => {
					this.props.updateCallState( data );

					successOverlay.open( {
						message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_SUCCESS_TRANSFER_CALL ),
					} );
				}
			);
		} else {

			// show error dialog
			if ( !isEmpty( data.description ) ) {
				errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
			} else {
				errorOverlay.open();
			}
		}
	}

	async initiateRetrieveCall( action = {} ) {
		log.debug( 'CallControlWidget.initiateRetrieveCall()' );

		// cancel ongoing request
		this.callControlWidgetService.cancel();

		// show page level preloader
		pageLevelPreloader.show();

		// get status and service data
		const { status, data } = await this.callControlWidgetService.retrieveCall( {
			url: action.url,
			data: {patientId: this.state.patientId}
		} );

		// hide page level preloader
		pageLevelPreloader.hide();

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
			this.setState(
				{
					isFetching: false,
					networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				},
				() => {
					this.props.updateCallState( data );
				}
			);
		} else {

			// show error dialog
			if ( !isEmpty( data.description ) ) {
				errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
			} else {
				errorOverlay.open();
			}
		}
	}

	/**
	 * @desc Handle call-end action button click.
	 */
	async handleCallEndAction( action = {} ) {
		log.debug( 'CallControlWidget.handleCallEndAction()' );

		const shouldProceed = await confirmOverlay.open( {
			message: getLabel( action.confirmation ),
			iconName: 'close'
		} );

		if ( ! shouldProceed ) {
			return;
		}

		/*--------*/

		// cancel ongoing request
		this.callControlWidgetService.cancel();

		// show page level preloader
		pageLevelPreloader.show();

		// get status and service data
		const { status, data } = await this.callControlWidgetService.endCall( {
			url: action.url,
			data: {patientId: this.state.patientId}
		} );

		// hide page level preloader
		pageLevelPreloader.hide();

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
			this.setState(
				{
					isFetching: false,
					networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				},
				() => {
					this.props.updateCallState( {
						callState: data.callState,
						callEndTime: data.callEndTime,
					} );
				}
			);
		} else {
			errorOverlay.open(); // show error
		}
	}

	/*********************************************/

	/**
	 * @desc When component unmounts, cleanup resouces.
	 */
	componentWillUnmount() {
		this.callControlWidgetService.cancel(); // cancel ongoing requests
	}

	/**
	 * @desc Render a React view component
	 */
	render() {

		// return `null` view if `callState` is `undefined`
		if ( isNil( this.props.callState ) ) {
			return null;
		}

		// getting the member Id
        const memberId = get( this.props.location, 'state.memberId', undefined );

		return (
			<CallControlWidgetView
				{...get( this.state, 'componentData', {} ) }
				isFetching={this.state.isFetching}
				networkStatus={this.state.networkStatus}
				isCallDispositionVisible={this.state.isCallDispositionVisible}
				callState={this.props.callState}
				callReasonValidationState={this.state.callReasonValidationState}
				callNotesValidationState={ this.state.callNotesValidationState }
				autoTaggingCallResolution={this.state.autoTaggingCallResolution}
				onCallControlActionClick={this.handleCallControlActionClick}
				onCallCategoryAndReasonsChange={this.handleCallCategoryAndReasonsChange}
				onCallNotesChange={ this.handleCallNotesChange }
				memberId={ memberId }
			/>
		);
	}
}

// set display name
_CallControlWidget.displayName = '_CallControlWidget';

// set default props
_CallControlWidget.defaultProps = {};

// prop types
_CallControlWidget.propTypes = {};

/*************************************************/

// map state to props
const mapStateToProps = ( state ) => {
	return {
		callState: state[ REDUCER.CALL_STATE ],
		appContext: state[ REDUCER.APP_CONTEXT ],
	};
};

// map dispatch to props
const mapDispatchToProps = ( dispatch ) => {
	return {
		initCallState: bindActionCreators( initCallState, dispatch ),
		updateCallState: bindActionCreators( updateCallState, dispatch ),
	};
};

// return component with acccess to Redux store
export const CallControlWidget = withRouter(
	connect( mapStateToProps, mapDispatchToProps )( _CallControlWidget )
);
